import { BaseSyntheticEvent, ChangeEvent, ReactElement, useCallback, useMemo } from 'react';
import { Selected } from '../../types';
import { flattenChildren, isOptionGroup } from './useFilteredItems';

interface UseSelectAllParams {
    items: ReactElement[];
    selected?: Selected;
    onChange?: (e: ChangeEvent, s: Selected) => void;
    onSelectAll?: (allSelected: boolean) => void;
}

interface SelectAllState {
    selectableItems: ReactElement[];
    selectedDisabledItems: ReactElement[];
    allSelected: boolean;
    indeterminate: boolean;
}

export const useSelectAll = ({ items, selected, onChange, onSelectAll }: UseSelectAllParams) => {
    const { selectableItems, selectedDisabledItems } = useMemo(() => {
        const flattened = flattenChildren(items);
        const withoutGroups = flattened.filter((item) => !isOptionGroup(item));
        return {
            selectableItems: withoutGroups.filter((item) => !item.props.disabled),
            selectedDisabledItems: withoutGroups.filter((item) => item.props.disabled && item.props.selected),
        };
    }, [items]);

    const selectionState = useMemo((): SelectAllState => {
        if (selectableItems.length === 0) {
            return {
                selectableItems,
                selectedDisabledItems,
                allSelected: false,
                indeterminate: false,
            };
        }

        const selectedCount = selectableItems.filter((item) => item.props.selected).length;

        return {
            selectableItems,
            selectedDisabledItems,
            allSelected: selectedCount === selectableItems.length,
            indeterminate: selectedCount > 0 && selectedCount < selectableItems.length,
        };
    }, [selectableItems, selectedDisabledItems]);

    const handleSelectAll = useCallback(
        (e: BaseSyntheticEvent | null) => {
            if (selectableItems.length === 0) return;

            const currentSelectAllValue = !selectionState.allSelected;
            const selectedDisabledValues = selectedDisabledItems.map((item) => item.props.value);
            const visibleSelectableValues = selectableItems.map((item) => item.props.value);
            const currentlySelected = selected ? [selected].flat() : [];

            const newValues = Array.from(
                new Set([
                    ...currentlySelected.filter(
                        (value) =>
                            selectedDisabledValues.includes(value) || // Keep disabled selected
                            !visibleSelectableValues.includes(value) // Keep non-visible selected
                    ),
                    ...(currentSelectAllValue ? visibleSelectableValues : []), // Add visible if selecting all
                ])
            );

            onChange?.(e as ChangeEvent, newValues);
            onSelectAll?.(currentSelectAllValue);
        },
        [selectableItems, selectedDisabledItems, selectionState.allSelected, selected, onChange, onSelectAll]
    );

    return {
        ...selectionState,
        handleSelectAll,
    };
};
